import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import {
  getWorkingHours,
  saveWorkingHours,
  WorkingHours,
  DaySchedule,
} from "../services/working-hours";
import { useNotification } from "./notification";

export interface WorkingHoursContextModel {
  workingHours: WorkingHours | null;
  loading: boolean;
  fetchWorkingHours: () => Promise<void>;
  updateWorkingHours: (
    data: Omit<WorkingHours, "id" | "updatedAt">
  ) => Promise<void>;
  updateDaySchedule: (day: string, schedule: DaySchedule) => Promise<void>;
}

const WorkingHoursContext = createContext<WorkingHoursContextModel>(
  {} as WorkingHoursContextModel
);

export const WorkingHoursProvider = ({ children }: { children: ReactNode }) => {
  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(null);
  const [loading, setLoading] = useState(false);
  const { handleNotification } = useNotification();

  const fetchWorkingHours = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWorkingHours();
      setWorkingHours(data);
    } catch (error: any) {
      handleNotification("error", "Erro ao buscar horário de expediente");
    } finally {
      setLoading(false);
    }
  }, [handleNotification]);

  const updateWorkingHours = useCallback(
    async (data: Omit<WorkingHours, "id" | "updatedAt">) => {
      setLoading(true);
      try {
        await saveWorkingHours(data);
        await fetchWorkingHours(); // Recarrega os dados atualizados
        handleNotification(
          "success",
          "Horário de expediente atualizado com sucesso!"
        );
      } catch (error: any) {
        handleNotification("error", "Erro ao atualizar horário de expediente");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchWorkingHours, handleNotification]
  );

  const updateDaySchedule = useCallback(
    async (day: string, schedule: DaySchedule) => {
      if (!workingHours) return;

      setLoading(true);
      try {
        const updatedData = {
          ...workingHours,
          [day]: schedule,
        };
        await saveWorkingHours(updatedData);
        await fetchWorkingHours(); // Recarrega os dados atualizados
        handleNotification("success", "Horário do dia atualizado com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao atualizar horário do dia");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [workingHours, fetchWorkingHours, handleNotification]
  );

  useEffect(() => {
    fetchWorkingHours();
  }, [fetchWorkingHours]);

  return (
    <WorkingHoursContext.Provider
      value={{
        workingHours,
        loading,
        fetchWorkingHours,
        updateWorkingHours,
        updateDaySchedule,
      }}
    >
      {children}
    </WorkingHoursContext.Provider>
  );
};

export const useWorkingHours = () => {
  const context = useContext(WorkingHoursContext);
  if (!context) {
    throw new Error(
      "useWorkingHours deve ser usado dentro de um WorkingHoursProvider"
    );
  }
  return context;
};
